import cn from 'classnames';
import { useState } from 'react';
import 'swiper/css';
import { Navigation, Pagination, A11y, Autoplay } from 'swiper/modules';
import { Swiper, SwiperSlide, useSwiper } from 'swiper/react';

import { setJumpLinkAttributes } from 'src/utils/jumpLinks';

import CaretLeft from 'assets/icons/caret-left.svg';
import PauseIcon from 'assets/icons/pause.svg';
import PlayIcon from 'assets/icons/play.svg';

import CtaSection from 'components/BaseHelpers/CtaSection';

import { ComponentProps } from '../../../types/component-props';
import { CoreCarouselData } from '../../../types/components';

import styles from './CoreCarousel.module.scss';

export type CoreCarouselProps = ComponentProps & CoreCarouselData;

const PrevButton = (): JSX.Element => {
  const swiper = useSwiper();
  return (
    <button className={styles.prev} onClick={() => swiper.slidePrev()} aria-label="Previous Slide">
      <CaretLeft className={styles['prev-icon']} />
    </button>
  );
};

const NextButton = (): JSX.Element => {
  const swiper = useSwiper();
  return (
    <button className={styles.next} onClick={() => swiper.slideNext()} aria-label="Next Slide">
      <CaretLeft className={styles['next-icon']} />
    </button>
  );
};

const PausePlayButton = (): JSX.Element => {
  const swiper = useSwiper();
  const [isPaused, setIsPaused] = useState(false);

  const handleClick = () => {
    if (!swiper.autoplay) return;

    if (isPaused) {
      swiper.autoplay.start();
      setIsPaused(false);
    } else {
      swiper.autoplay.stop();
      setIsPaused(true);
    }
  };

  return (
    <button className={styles['pause-play-button']} onClick={handleClick}>
      {isPaused ? <PlayIcon className={styles.play} /> : <PauseIcon className={styles.pause} />}
    </button>
  );
};
const CoreCarousel = (props: CoreCarouselProps): JSX.Element => {
  const { items, autoplayDelayMs, jumpLinkLabel } = props.fields;

  const itemsToRender = items.filter((item) => item.title && item.description);

  if (itemsToRender.length === 0) {
    return <></>;
  }

  return (
    <div
      className={cn(styles.main, 'container-12')}
      {...setJumpLinkAttributes(jumpLinkLabel)}
      data-component="CoreCarousel"
    >
      <Swiper
        slidesPerView={1}
        modules={[Navigation, Pagination, A11y, Autoplay]}
        pagination={{
          bulletActiveClass: styles['page--active'],
          bulletClass: styles.page,
          bulletElement: 'button',
          clickable: true,
          el: `.${styles.pagination}`,
        }}
        autoplay={autoplayDelayMs ? { delay: autoplayDelayMs } : false}
        loop={true}
        className={styles.swiper}
        autoHeight={true}
      >
        {itemsToRender.map((item, index) => (
          <SwiperSlide key={index} className="swiper-slide">
            <CtaSection
              key={index}
              eyebrow={item.eyebrow}
              heading={item.title}
              description={item.description}
              image={item.image}
              link={item.link}
            />
          </SwiperSlide>
        ))}
        {itemsToRender.length > 1 && (
          <div className={cn(styles.controls, 'container-10')}>
            <div className={styles['controls-wrapper']}>
              <div className={styles['pagination-controls']}>
                <PausePlayButton />
                <div className={styles.pagination}></div>
              </div>
              <div className={styles['navigation-buttons']}>
                <PrevButton />
                <NextButton />
              </div>
            </div>
          </div>
        )}
      </Swiper>
    </div>
  );
};

export default CoreCarousel;
